import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router";

import { AuthLoading } from "@/components/auth/auth-loading";
import Icon from "@/components/icon";
import { DrugSearchError } from "@/components/report/drug-search-error";
import { SearchBox } from "@/components/search/search-box";
import { toSearchSlug } from "@/components/search/suggestion-rows";
import { useAuth } from "@/context/auth-context";
import { useDrugSearch } from "@/hooks/use-drug-search";
import { trackReportViewed } from "@/lib/analytics";
import {
  formatLibraryReportCount,
  useLibraryReportCount,
} from "@/hooks/use-library-report-count";
import { useRecentSearches } from "@/hooks/use-recent-searches";
import { showHowItWorks } from "@/routes/home-intro";
import { HomeHero, HomeShortcuts } from "@/views/home-view";
import { HowItWorks } from "@/views/how-it-works";
import LandingView from "@/views/landing-view";
import {
  InlineReportLoader,
  SearchLoadingView,
} from "@/views/search-loading-view";

// The result view pulls in the whole report tree — by far the heaviest part of
// the bundle — so it's code-split: signed-out landing/login cold loads never
// download it, and an effect below warms the chunk right after sign-in so the
// Suspense fallback almost never paints.
const loadSearchResultView = () => import("@/views/search-result-view");
const SearchResultView = lazy(loadSearchResultView);

// Thin composer for "/" and "/:query": auth gating, then switches between the
// landing / home / loading / result views. The URL is the source of truth for
// what's being searched (links like /N02BE01 are shareable, the logo resets
// state), useDrugSearch owns the search lifecycle, and SearchBox stays mounted
// across every state so the layout never jumps.
function Home() {
  // The human label currently shown in the box for the active search (e.g.
  // "Paracetamolum" or "Ibuprofenum (na skórę)"). Owned here because the search
  // hook labels history with it and the result view renders it; SearchBox keeps
  // it in sync with the URL. Cleared on edit / reset.
  const [committedLabel, setCommittedLabel] = useState<string | null>(null);
  const {
    user,
    loading: authLoading,
    profileLoading,
    needsOnboarding,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { query: queryParam } = useParams();
  const decodedQuery = queryParam ? decodeURIComponent(queryParam) : "";
  // The human label only rides along on a warm, in-app navigation (a typeahead
  // pick). A shared / refreshed link has no state — so the box label is then
  // reconstructed from the URL (inside SearchBox) instead of showing the raw
  // ATC code.
  const stateLabel =
    (location.state as { label?: string } | null)?.label ?? null;

  const {
    drugSearch,
    fastPathDrugId,
    fastPathLoading,
    isLoading,
    searchUiError,
    retrying,
    reportState,
    displayReport,
    displayReportVersion,
    displayGeneratedAt,
    retrySearch,
    reportStuckSearch,
  } = useDrugSearch({ user, decodedQuery, committedLabel });

  // Per-user history for the empty homepage: returning users get their recent
  // searches (tapping one reattaches the cached report instantly) and a compact
  // hero; first-run users get the full hero and the workflow strip.
  const { recents, hasHistory } = useRecentSearches(user?.uid);
  const { count: libraryReportCount } = useLibraryReportCount(
    Boolean(
      user &&
        !authLoading &&
        !profileLoading &&
        !needsOnboarding &&
        !decodedQuery &&
        !drugSearch &&
        !fastPathDrugId,
    ),
  );
  const libraryReportCountLabel =
    formatLibraryReportCount(libraryReportCount);

  useEffect(() => {
    if (user) void loadSearchResultView();
  }, [user]);

  // One event per distinct report view. The route query goes to PostHog
  // deliberately (product decision); the drug id / version key is only a
  // local dedupe guard. Leaving the report resets the guard, so a genuine
  // revisit counts as a new view while re-renders of the same report do not.
  const viewedReportRef = useRef<string | null>(null);
  const viewedDrugId = fastPathDrugId ?? drugSearch?.results?.drug_id;
  useEffect(() => {
    if (reportState !== "ready" || !displayReport) {
      viewedReportRef.current = null;
      return;
    }
    const key = `${viewedDrugId ?? ""}:${displayReportVersion ?? ""}`;
    if (viewedReportRef.current === key) return;
    viewedReportRef.current = key;
    trackReportViewed(decodedQuery);
  }, [reportState, displayReport, viewedDrugId, displayReportVersion, decodedQuery]);

  // Drive searches through the URL so they're shareable and back/forward work.
  // The path is a canonical slug (sorted, per-token-encoded, "+"-joined for a
  // combination) so a multi-substance search reads as "J01CA04+J01CR02" rather
  // than "J01CA04%20%2B%20J01CR02" and order-independent picks share one report.
  const goToSearch = (query: string, label?: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    const slug = toSearchSlug(trimmed);
    // Same URL after a failure would otherwise no-op (the hook keys searches by
    // query). Retry.
    if (
      slug === decodedQuery &&
      (drugSearch?.status === "failed" || searchUiError)
    ) {
      void retrySearch();
      return;
    }
    navigate(`/${slug}`, {
      state: label ? { label } : undefined,
    });
  };

  if (authLoading || (user && profileLoading)) {
    return <AuthLoading />;
  }

  const returnTo = `${location.pathname}${location.search}`;

  if (!user) {
    // Deep-linked searches still require sign-in; bare `/` is the landing page.
    if (decodedQuery) {
      return <Navigate to="/login" replace state={{ from: returnTo }} />;
    }
    return <LandingView />;
  }

  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace state={{ from: returnTo }} />;
  }

  const hasActiveSearch = Boolean(drugSearch || fastPathDrugId);
  // The intro sections live ONLY on the empty homepage (showIntro excludes
  // the search / loading / report / error states). Recents and example
  // reports stay for every user; the workflow strip is onboarding and is gone
  // for good once the doctor has search history (decision 2026-11-03).
  const showIntro = !decodedQuery && !hasActiveSearch;

  return (
    <div className="flex flex-col gap-6 sm:gap-8">
      {showIntro && <HomeHero compact={hasHistory} />}

      <SearchBox
        decodedQuery={decodedQuery}
        stateLabel={stateLabel}
        isLoading={isLoading}
        docked={hasActiveSearch}
        committedLabel={committedLabel}
        onCommittedLabelChange={setCommittedLabel}
        onSearch={goToSearch}
      />

      {/* Explicit way back for users who do not know the logo resets the app.
          Always navigates to "/" (never browser back): history can hold /login
          or an external referrer after a shared link. The label names the task
          (a new search), not the destination — and the link is foreground, not
          muted: pilot testers missed the quiet version. */}
      {!showIntro && (
        <Link
          to="/"
          className="inline-flex w-fit items-center gap-1 text-sm font-medium text-foreground transition-colors hover:text-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Icon name="chevron-left" className="h-4 w-4" aria-hidden="true" />
          Nowe wyszukiwanie
        </Link>
      )}

      {/* Reserve one text line while the aggregate count loads so the intro
          sections do not move. */}
      {showIntro && (
        <p
          className="mx-auto -mt-2 min-h-6 w-full max-w-2xl text-center text-sm leading-6 text-muted-foreground sm:-mt-3"
          aria-live="polite"
        >
          {libraryReportCountLabel}
        </p>
      )}

      {showHowItWorks(showIntro, hasHistory) && (
        <HowItWorks variant="compact" />
      )}

      {showIntro && <HomeShortcuts recents={recents} onOpen={goToSearch} />}

      {searchUiError && (
        <div
          className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3"
          role="alert"
        >
          <p className="text-sm text-destructive">{searchUiError}</p>
          {decodedQuery && (
            <button
              type="button"
              onClick={() => void retrySearch()}
              disabled={retrying}
              className="mt-2 text-sm font-medium text-brand hover:underline disabled:opacity-60"
            >
              {retrying ? "Ponawiamy…" : "Spróbuj ponownie"}
            </button>
          )}
        </div>
      )}

      {hasActiveSearch && (
        <div className="flex flex-col gap-6 sm:gap-8">
          {isLoading && (
            <SearchLoadingView
              fastPath={fastPathLoading}
              progress={drugSearch?.progress}
              startedAt={drugSearch?.created_at}
              retrying={retrying}
              onRetry={() => void retrySearch()}
              onReportStuck={() => void reportStuckSearch()}
            />
          )}

          {drugSearch?.status === "failed" && (
            <DrugSearchError
              results={drugSearch.results}
              searchId={drugSearch.id}
              query={drugSearch.query}
              onRetry={() => void retrySearch()}
              retrying={retrying}
            />
          )}

          {/* Gated on the LIBRARY report being resolved, not just on the search
              being completed: a completed search is only a pointer into `drugs`,
              and an unresolvable one is regenerated (see useDrugSearch) rather
              than rendered. */}
          {reportState === "ready" && displayReport && (
              <Suspense fallback={<InlineReportLoader />}>
                <SearchResultView
                  report={displayReport}
                  reportVersion={displayReportVersion}
                  generatedAt={displayGeneratedAt}
                  drugId={fastPathDrugId ?? drugSearch?.results?.drug_id}
                  query={drugSearch?.query ?? decodedQuery}
                  displayName={committedLabel ?? undefined}
                />
              </Suspense>
            )}
        </div>
      )}
    </div>
  );
}

export default Home;
